import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { Tabs, Tab } from '@mui/material'

import CourseForm from 'components/form/course/CourseForm'
import CourseProviderMapping from 'components/form/course/CourseProviderMapping'
import CourseGps from 'components/form/course/CourseGps'
import ClubModalForm from 'components/form/ClubModalForm'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'

import { clearCourse, selectCourse } from 'store/course/slice'
import { getCourse } from 'store/course/thunks'
import CourseStats from 'components/form/course/CourseStats'

enum COURSE_TAB {
  CourseData = 'courseData',
  GPS = 'gps',
  ProviderMapping = 'providerMapping',
  CourseStats = 'courseStats',
}

interface ParamTypes extends ParamTypesBase {
  clubId: string
  courseId: string
}

export default function Course(): JSX.Element {
  const { clubId, courseId } = useParams<ParamTypes>()
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  let tabParam = searchParams.get('tab') as COURSE_TAB | null
  if (tabParam && Object.values(COURSE_TAB).includes(tabParam) === false) {
    navigate('?')
    tabParam = null
  }
  const initialTab = tabParam || COURSE_TAB.CourseData
  const [selectedTab, setSelectedTab] = useState<string>(initialTab)
  const [clubModalOpen, setClubModalOpen] = useState<boolean>(false)
  const { course, status } = useSelector(selectCourse)

  useEffect(() => {
    const currentCourseId = courseId ? parseInt(courseId) : null
    if (currentCourseId && (!course || course.id !== currentCourseId)) {
      dispatch(clearCourse())
      dispatch(getCourse(currentCourseId))
    }
  }, [course, courseId, dispatch])

  useEffect(() => {
    return () => {
      dispatch(clearCourse())
    }
  }, [dispatch])

  const handleTabChange = (e: any, tab: string) => {
    const search = `?tab=${tab}`
    navigate(search)
    setSelectedTab(tab)
  }

  const toggleClubModal = () => {
    setClubModalOpen(!clubModalOpen)
  }

  if (status === 'loading') {
    return (
      <LoaderContainer>
        <LoadingSpinnerGreen size={92} />
      </LoaderContainer>
    )
  }

  return (
    <PageContainer>
      <ActionContainer>
        <TabContainer value={selectedTab} onChange={handleTabChange}>
          <Tab value={COURSE_TAB.CourseData} label={<FormattedMessage id="strings.courseData" />} />
          <Tab value={COURSE_TAB.GPS} label={<FormattedMessage id="strings.gps" />} />
          <Tab value={COURSE_TAB.ProviderMapping} label={<FormattedMessage id="strings.providerMapping" />} />
          <Tab value={COURSE_TAB.CourseStats} label={<FormattedMessage id="strings.courseHoleStats" />} />
        </TabContainer>
      </ActionContainer>

      {selectedTab === COURSE_TAB.CourseData && (
        <CourseForm course={course} clubId={clubId ? parseInt(clubId) : null} toggleClubModal={toggleClubModal} />
      )}
      {selectedTab === COURSE_TAB.GPS && (
        <CourseGps course={course} clubId={clubId ? parseInt(clubId) : null} toggleClubModal={toggleClubModal} />
      )}
      {selectedTab === COURSE_TAB.ProviderMapping && (
        <CourseProviderMapping
          course={course}
          clubId={clubId ? parseInt(clubId) : null}
          toggleClubModal={toggleClubModal}
        />
      )}
      {selectedTab === COURSE_TAB.CourseStats && <CourseStats course={course} />}
      {course.club?.id && clubModalOpen && (
        <ClubModalForm open={clubModalOpen} toggle={toggleClubModal} clubId={course.club?.id} />
      )}
    </PageContainer>
  )
}

const PageContainer = styled.div`
  margin: 10px;
  padding: 10px 0 20px 0;
  min-height: 500px;
  background-color: ${(props) => props.theme.customPalette.lightGray};

  & a {
    color: ${(props) => props.theme.palette.primary.main};
    text-decoration: none;
  }
`

const ActionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 0 0 40px;
`

const TabContainer = styled(Tabs)`
  width: 50%;
  min-height: 30px;

  & .MuiTabs-indicator {
    height: 0;
  }

  & button {
    background-color: ${(props) => props.theme.customPalette.mediumGray};
    color: #242424;
    border-radius: 9px 9px 0 0;
    min-height: 30px;
    text-transform: none;

    &.Mui-selected {
      background-color: ${(props) => props.theme.customPalette.light};
      color: ${(props) => props.theme.palette.primary.main};
    }
  }
`

const LoaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 50px 0;
`
